import React, { useState, useRef } from 'react';

import classNames from 'classnames';
import { NavLink, useNavigate } from 'react-router-dom';

import { useOutsideHandler, useAuth } from '../hooks';
import BrainHiLogo from '../images/brainhi_logo_white.svg';
import { IS_MIRAGE } from '../utils';

const Navbar: React.FC = () => {
  const menuRef = useRef(null);
  const [isMobileNavbarOpen, setIsMobileNavbarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { connectedUser, logout } = useAuth();

  useOutsideHandler(menuRef, () => setIsUserMenuOpen(false));

  const handleGoHome = (): void => {
    navigate('/');
  };

  const handleLogout = (): void => {
    logout();
  };

  const handleGoToUserProfile = (): void => {
    navigate('user_profile');
    setIsUserMenuOpen(false);
  };

  const RenderStandard: React.FC = () => {
    return (
      <div className="sm:px-6 lg:px-8 px-4 mx-auto">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div
              className="flex-shrink-0 cursor-pointer"
              onClick={handleGoHome}
              role="presentation"
            >
              <img className="w-24" src={BrainHiLogo} alt="BrainHi logo" />
            </div>
            <div className="md:block hidden">
              <div className="flex ml-10 space-x-4">
                <NavLink
                  to="accounts"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Accounts
                </NavLink>
                <NavLink
                  to="call_rescue_monitor"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Call rescue monitor
                </NavLink>
                <NavLink
                  to="tcr"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  TCR
                </NavLink>
                <NavLink
                  to="phone_settings"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Phone
                </NavLink>
                <NavLink
                  to="wl_devices"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  WL Devices
                </NavLink>
                <NavLink
                  to="provisioning_requests"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Requests
                </NavLink>
                <NavLink
                  to="support"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Support
                </NavLink>
                <NavLink
                  to="survey_conversion"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Survey conversion
                </NavLink>
                {!IS_MIRAGE && (
                  <NavLink
                    to="citasmedicas"
                    className={({ isActive }) =>
                      classNames(
                        'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                        isActive && 'bg-primary-3.0'
                      )
                    }
                  >
                    citas.pr
                  </NavLink>
                )}
                <NavLink
                  to="product_updates"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  Product Updates
                </NavLink>
                <NavLink
                  to="api"
                  className={({ isActive }) =>
                    classNames(
                      'hover:bg-primary-3.0 focus:text-white-1.0 focus:bg-primary-3.0 px-3 py-2 text-white-1.0 rounded',
                      isActive && 'bg-primary-3.0'
                    )
                  }
                >
                  API
                </NavLink>
              </div>
            </div>
          </div>
          <div className="md:block hidden">
            <div className="md:ml-6 flex items-center ml-4">
              <div className="relative ml-3" onBlur={() => setIsUserMenuOpen(false)}>
                <button
                  className="flex w-8 h-8 text-sm rounded-full bg-white-1.0"
                  id="user-menu"
                  aria-label="User menu"
                  aria-haspopup="true"
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <i className="icon-user_icon m-auto text-xl" />
                </button>
                <div
                  className={classNames(
                    'absolute right-0 w-48 mt-2 origin-top-right rounded shadow-lg z-50 bg-white-0.0 py-2',
                    isUserMenuOpen ? 'block' : 'hidden'
                  )}
                  ref={menuRef}
                >
                  <div role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                    <p
                      className="hover:bg-white-1.0 block px-4 py-2 cursor-pointer"
                      role="presentation"
                      onClick={handleGoToUserProfile}
                    >
                      Profile
                    </p>
                  </div>
                  <div role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                    <p
                      className="hover:bg-white-1.0 block px-4 py-2 cursor-pointer"
                      role="presentation"
                      onClick={handleLogout}
                    >
                      Sign out
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:hidden flex -mr-2">
            <button
              className="inline-flex"
              type="button"
              onClick={() => setIsMobileNavbarOpen(!isMobileNavbarOpen)}
            >
              <svg
                className={classNames('w-6 h-6', !isMobileNavbarOpen ? 'block' : 'hidden')}
                stroke="white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <i
                className={classNames(
                  'icon-reject_icon text-2xl text-white-0.0',
                  isMobileNavbarOpen ? 'block' : 'hidden'
                )}
              />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RenderMovil: React.FC = () => {
    return (
      <div className={classNames('md:hidden', isMobileNavbarOpen ? 'block' : 'hidden')}>
        <div className="sm:px-3 px-2 pt-2 pb-3 space-y-1">
          <NavLink
            to="accounts"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Account
          </NavLink>
          <NavLink
            to="call_rescue_monitor"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Call rescue monitor
          </NavLink>
          <NavLink
            to="tcr"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            TCR
          </NavLink>
          <NavLink
            to="phone_settings"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Phone
          </NavLink>
          <NavLink
            to="wl_devices"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            WL Devices
          </NavLink>
          <NavLink
            to="provisioning_requests"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Requests
          </NavLink>
          <NavLink
            to="support"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Support
          </NavLink>
          <NavLink
            to="survey_conversion"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Survey conversion
          </NavLink>
          {!IS_MIRAGE && (
            <NavLink
              to="citasmedicas"
              className={({ isActive }) =>
                classNames(
                  'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                  isActive && 'bg-primary-3.0'
                )
              }
            >
              citas.pr
            </NavLink>
          )}
          <NavLink
            to="product_updates"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            Product Updates
          </NavLink>
          <NavLink
            to="api"
            className={({ isActive }) =>
              classNames(
                'focus:outline-none focus:text-white-0.0 focus:bg-primary-3.0 block px-3 py-2 text-base font-medium text-white-0.0 rounded',
                isActive && 'bg-primary-3.0'
              )
            }
          >
            API
          </NavLink>
        </div>
        <div className="pt-4 pb-3 border-t border-gray-3.0">
          <div
            role="presentation"
            className="flex items-center px-5 space-x-3 cursor-pointer"
            onClick={handleGoToUserProfile}
          >
            <div className="flex flex-shrink-0 w-10 h-10 rounded-full bg-white-1.0">
              <i className="icon-user_icon m-auto text-xl" />
            </div>
            <div className="space-y-1">
              <p className="oversize text-white-1.0">{connectedUser?.username || ''}</p>
              <p className="text-white-1.0">{connectedUser?.email || ''}</p>
            </div>
          </div>
          <div className="px-2 mt-3 space-y-1" role="presentation" onClick={handleLogout}>
            <p className="hover:text-white-1.0 hover:bg-primary-3.0 focus:outline-none block px-3 py-2 text-white-0.0 rounded">
              Sign out
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <nav className="bg-primary-2.0 sticky top-0">
      <RenderStandard />
      <RenderMovil />
    </nav>
  );
};

export default Navbar;
